// Shared utilities used by both the sorters and the filter popups.
//
// Every cell value reaching Tabulator is an HTML string (formatter: 'html'
// preserves links/badges/components rendered via HAML). The default
// sorters/filters would compare markup, not content — these helpers pull
// the comparable bit out so sort + filter logic can work with text /
// numbers / ISO datetimes instead of raw HTML.

// Read the inner `<time datetime="...">` ISO value out of a cell. The
// `temporal` sorter uses this so sort order is chronological regardless
// of the visible display format (e.g. "Today | 9:17 am").
export function extractDatetimeAttr(value) {
  if (typeof value !== 'string') return '';
  const sortValueMatch = /<span\b[^>]*\sdata-sort-value="([^"]*)"/.exec(value);
  if (sortValueMatch) return sortValueMatch[1];

  const match = /<time\b[^>]*\sdatetime="([^"]+)"/.exec(value);
  return match ? match[1] : value;
}

// Read the `data-sort-value` off a `<span data-sort-value="...">` wrapper
// the controller adds when the source `<td>` carried a `data-value`
// attribute. Lets callers display one thing and sort/filter by another
// (e.g. "High" displayed, "2" used for ordering).
export function extractSortValue(value) {
  if (typeof value !== 'string') return value;
  const match = /<span\b[^>]*\sdata-sort-value="([^"]*)"/.exec(value);
  return match ? match[1] : value;
}

// Strip the HTML markup off a cell value and return the visible text —
// what filter UIs and option-list extraction compare against.
//
// Parses via `DOMParser` rather than assigning to a detached `<div>`'s
// `innerHTML`. Both reliably suppress `<script>` execution, but the
// `innerHTML` path still kicks off resource loads from `<img onerror>`
// and similar in jsdom + a few browser test runners. `DOMParser` builds
// an inert document — no fetches, no side effects — which keeps unit
// runs deterministic and the cell-text path safe to call on every
// keystroke when global search is active.
const HTML_PARSER = new DOMParser();

export function extractCellText(raw) {
  if (raw == null) return '';
  const doc = HTML_PARSER.parseFromString(String(raw), 'text/html');
  return doc.body.textContent.trim();
}

// Key used to compare a cell against checkbox-filter options.
//
// Prefers the dedicated `data-filter-value` wrapper attribute when present —
// this lets a cell sort/export by one value (`data-sort-value`, e.g. an
// iso8601 end-instant) while the checkbox filter still groups by another (e.g.
// Yes/No). Falls back to `data-sort-value` (the string/number sort override,
// used by columns that filter and sort on the same value), then to visible
// text for cells with neither. Both regexes use `[^>]*` before the attribute
// so they match regardless of attribute order within the span tag.
export function extractFilterKey(raw) {
  if (typeof raw === 'string') {
    const filterMatch = /<span\b[^>]*\sdata-filter-value="([^"]*)"/.exec(raw);
    if (filterMatch) return filterMatch[1];
    const sortMatch = /<span\b[^>]*\sdata-sort-value="([^"]*)"/.exec(raw);
    if (sortMatch) return sortMatch[1];
  }
  return extractCellText(raw);
}

// Convert a free-text caption to a filesystem-safe filename slug. Used
// when generating the CSV download filename so consumers don't have to
// pass anything beyond the existing `caption:` attribute.
export function slugify(value) {
  return String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Today's local date as `11-Jan-2026`. Used for stamping the CSV filename
// so re-exports don't overwrite each other on the user's machine. The month
// is abbreviated to a name rather than a number so UK-format dates can't be
// misread as US `MM-DD-YYYY` by international customers.
const FILENAME_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function todayFilenameDate() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  return `${day}-${FILENAME_MONTHS[now.getMonth()]}-${now.getFullYear()}`;
}
